import * as ko from "knockout";
import * as moment from "moment";
import * as localisationHelper from "localisationHelper";
import * as _ from "underscore";

// Types
import Moment = moment.Moment;

// Helpers
import "dateTimeBindings";
import "fontAwesomeBindings";
import "knockout.validation";
import "tooltipPopoverBindings";

// Enums
import { DateTimeWorkflowStates } from "Content/ts/Shared/Controls/DateTime/Services/workflowStates";

// Models
import { DateTimeConfiguration, DateTimeValidationConfiguration } from "Content/ts/Models/Shared/DateTime/dateTimePickerConfiguration";

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { TimePickerServices } from "Content/ts/Shared/KnockoutBindings/DateTime/Services/timePickerServices";
import { DateTimePickerServices } from "Content/ts/Shared/Controls/DateTime/Services/dateTimePickerServices";
import { UniqueIdentifierServices } from "Content/ts/Shared/KnockoutBindings/Services/uniqueIdentifierServices";
import { DateTimeFormatterService } from "Content/ts/Shared/Controls/DateTime/Services/dateTimeFormatterService";
import { DateTimePickerValidationServices } from "Content/ts/Shared/Controls/DateTime/Services/dateTimePickerValidationServices";

// Components
import "Content/ts/Shared/Buttons/StandardButtons/StandardButtonsBase";
import "Content/ts/Shared/BootstrapAlerts/AlertValidation/alert-validation";

export const Deps = {
    LocalisationHelper: localisationHelper,
    TimePickerServices: TimePickerServices
}

export interface IDateTimePicker {
    Value: KnockoutObservable<Moment>;                                                          // Required
    Configuration: KnockoutObservable<DateTimeConfiguration> | DateTimeConfiguration;           // Required

    ValidationFields?: KnockoutObservable<any>[];                                               // Optional
    DisplayCondition?: boolean | KnockoutObservable<boolean>;                                   // Optional
    TabToCalendarButton: boolean;                                                               // Optional
}

export default class DateTimePicker {
    CustomMessages: {
        ClearDateTime: string;
    };

    private defaultTime: string;
    private defaultDate: string;

    datePickerIdentifier: string;
    timePickerIdentifier: string;
    DateFormatPlaceholder: string;
    TimeFormatPlaceholder: string;
    fullLengthRemoveButton: boolean;
    hasDateConfigurationProvided: boolean;
    hasTimeConfigurationProvided: boolean;

    dateLabel: string | KnockoutObservable<string>;
    timeLabel: string | KnockoutObservable<string>;
    isTimeRequired: boolean | KnockoutObservable<boolean>;
    isDateRequired: boolean | KnockoutObservable<boolean>;
    DisplayCondition: boolean | KnockoutObservable<boolean>;

    configuration: DateTimeConfiguration;

    hasSetDateProperty: KnockoutObservable<boolean>;
    hasSetTimeProperty: KnockoutObservable<boolean>;

    value: KnockoutObservable<Moment>;
    validationFields: KnockoutObservable<any>[];
    ReadonlyDateValue: KnockoutObservable<string>;
    ReadonlyTimeValue: KnockoutObservable<string>;

    dateValue: KnockoutComputed<Moment>;
    timeValue: KnockoutComputed<Moment>;
    isDateReadOnly: KnockoutComputed<boolean>;
    isTimeReadOnly: KnockoutComputed<boolean>;
    displayRemoveTime: KnockoutComputed<boolean>;
    displayDatePicker: KnockoutComputed<boolean>;
    displayTimePicker: KnockoutComputed<boolean>;
    isDateTimePickerMode: KnockoutComputed<boolean>;
    displayRemoveDateTime: KnockoutComputed<boolean>;
    formattedDatePlaceholderLabel: KnockoutComputed<string>;
    formattedTimePlaceholderLabel: KnockoutComputed<string>;

    dateTimeFormat: string;

    subs: KnockoutSubscription[];

    constructor(params: IDateTimePicker) {
        // Component Validation
        let config = ko.unwrap(params.Configuration);
        if (!params.Value || !config) {
            throw new Error("Please ensure all required properties are provided when referencing the 'datetime-picker' component");
        }

        // Provided Component Properties
        this.value = params.Value;
        this.dateLabel = config.DateLabel;
        this.timeLabel = config.TimeLabel;
        this.validationFields = params.ValidationFields || [];
        this.DisplayCondition = params.DisplayCondition || true; 

        this.configuration = DateTimePickerServices.ConfigureConfigurationProperties(config);
        this.configuration.DateConfiguration.TabToCalendarButton = params.TabToCalendarButton || false;

        this.subs = [];

        // Standard Component Properties
        const today = moment().startOf("date");
        this.defaultTime = today.format(DateTimeFormatterService.InternalMomentTimeFormat);
        this.defaultDate = today.format(DateTimeFormatterService.InternalMomentDateFormat);

        this.fullLengthRemoveButton = true;
        this.isDateRequired = this.configuration.DateConfiguration.IsRequired;
        this.isTimeRequired = this.configuration.TimeConfiguration.IsRequired;

        this.ReadonlyDateValue = ko.observable(null);
        this.ReadonlyTimeValue = ko.observable(null);
        this.hasSetDateProperty = ko.observable(false);
        this.hasSetTimeProperty = ko.observable(false);

        this.TimeFormatPlaceholder = DateTimeFormatterService.LocalisedMomentTimeFormat;
        this.DateFormatPlaceholder = DateTimeFormatterService.LocalisedMomentShortDateFormat;
        this.hasDateConfigurationProvided = DateTimePickerServices.HasDateConfigurationProvided(this.configuration);
        this.hasTimeConfigurationProvided = DateTimePickerServices.HasTimeConfigurationProvided(this.configuration);

        // Initialisation Methods
        this.RetrieveCustomMessages();

        this.ComputedMethods();
        this.SetupValidation();
        this.SubscribeMethods();
        this.ConfigureUniqueIdentifiers();
        this.ConfigureDateTimeProperties();

        // Important    :   Value needs to be updated after the computed and subscribe methods have been configured. If they aren't 
        //                  then the dependencies won't work correctly and will subsequently throw exceptions in the console.
        this.ReadonlyDateValue(this.ParseDateToReadOnlyValue(this.dateValue()));
        this.ReadonlyTimeValue(this.ParseTimeToReadOnlyValue(this.timeValue()));

        this.dateTimeFormat = `${DateTimeFormatterService.InternalMomentDateFormat} ${DateTimeFormatterService.InternalMomentTimeFormat}`;
    }

    public ClearDateTime(): void {
        this.value(null);

        this.hasSetDateProperty(false);
        this.hasSetTimeProperty(false);
    };

    // Purpose  :   When navigating into the workflow for the first time ensure the overrides are configured
    //              correctly.If this doesn't happen the read/write computeds will return null when a value
    //              has already been predefined.
    private ConfigureDateTimeProperties(): void {
        const hasValue = this.value() != null && this.value() != undefined;

        if (this.configuration.ComponentState == DateTimeWorkflowStates.DateTimeMode) {
            this.hasSetDateProperty(hasValue);
            this.hasSetTimeProperty(hasValue);
        }

        if (this.configuration.ComponentState == DateTimeWorkflowStates.DateMode) {
            this.hasSetDateProperty(hasValue);
        }

        if (this.configuration.ComponentState == DateTimeWorkflowStates.TimeMode) {
            this.hasSetTimeProperty(hasValue);
        }
    };

    // Purpose  :   Both the date and time picker values are bound to the same object but the UI
    //              will render both options. It's not possible to assign them through the UID
    //              binding without causing a race condition. With that in mind the TS side of the
    //              component will need to be in charge of handling the unique identifiers.
    private ConfigureUniqueIdentifiers(): void {
        this.datePickerIdentifier = UniqueIdentifierServices.AddNewIdentifier(this.value);
        UniqueIdentifierServices.RemoveLinkHash(this.value);

        this.timePickerIdentifier = UniqueIdentifierServices.AddNewIdentifier(this.value);
        UniqueIdentifierServices.RemoveLinkHash(this.value);
    }

    private ParseDateToReadOnlyValue(datePickerValue: Moment): string {
        if (datePickerValue == null || datePickerValue == undefined) {
            return null;
        }

        return datePickerValue.format(DateTimeFormatterService.LocalisedMomentShortDateFormat);
    }


    private ParseTimeToReadOnlyValue(datePickerValue: Moment): string {
        if (datePickerValue == null || datePickerValue === undefined) {
            return null;
        }

        return datePickerValue.format(DateTimeFormatterService.LocalisedMomentTimeFormat);
    }

    private ComputedMethods(): void {
        this.displayDatePicker = ko.pureComputed(() => {
            return this.configuration.ComponentState == DateTimeWorkflowStates.DateMode || this.configuration.ComponentState == DateTimeWorkflowStates.DateTimeMode;
        });

        this.displayTimePicker = ko.pureComputed(() => {
            return this.configuration.ComponentState == DateTimeWorkflowStates.TimeMode || this.configuration.ComponentState == DateTimeWorkflowStates.DateTimeMode;
        });

        this.displayRemoveDateTime = ko.pureComputed(() => {
            return ko.unwrap(this.configuration.AllowDateTimeClear) && this.configuration.ComponentState == DateTimeWorkflowStates.DateTimeMode && this.value() != null;
        });

        this.displayRemoveTime = ko.pureComputed(() => {
            return this.configuration.ComponentState == DateTimeWorkflowStates.TimeMode && this.value() && this.hasTimeConfigurationProvided &&
                this.configuration.TimeConfiguration.AllowClear && this.value() != null;
        });

        this.isDateTimePickerMode = ko.pureComputed(() => {
            return this.configuration.ComponentState == DateTimeWorkflowStates.DateTimeMode;
        });

        this.isDateReadOnly = ko.pureComputed(() => {
            return ko.unwrap(this.configuration.DateConfiguration.IsReadonly);
        });

        this.isTimeReadOnly = ko.pureComputed(() => {
            return ko.unwrap(this.configuration.TimeConfiguration.IsReadonly);
        });

        this.timeValue = ko.computed({
            owner: this,
            read: (): Moment => {
                return DateTimePickerServices.FormatDateTimeComputedRead(this.value, this.hasSetTimeProperty, this.configuration.ComponentState);
            },
            write: (value: Moment) => {
                this.hasSetTimeProperty(value != null);

                let currentDateValue = this.defaultDate,
                    currentTimeValue = value ? value.format(DateTimeFormatterService.InternalMomentTimeFormat) : this.defaultTime;

                if (this.value()) {
                    currentDateValue = moment(this.value()).format(DateTimeFormatterService.InternalMomentDateFormat);
                }

                if (value) {
                    switch (this.configuration.ComponentState) {
                        case DateTimeWorkflowStates.DateMode:
                            // Important    :   Don't need to set the time, just autofill it as the start of current day
                            this.value(moment(`${currentDateValue} ${this.defaultTime}`, this.dateTimeFormat));
                            break;
                        case DateTimeWorkflowStates.TimeMode:
                            // Important    :   Don't need to set the date, just autofill it as today's date.
                            this.value(moment(`${this.defaultDate} ${currentTimeValue}`, this.dateTimeFormat));
                            break;
                        case DateTimeWorkflowStates.DateTimeMode:
                            // Important    :   If the date has been set then ensure the value is set to the assigned
                            //                  object. In the event it's not been set use today's date in order to
                            //                  ensure the moment object is valid.
                            if (this.hasSetDateProperty()) {
                                this.value(moment(`${currentDateValue} ${currentTimeValue}`, this.dateTimeFormat));
                            } else {
                                this.value(moment(`${this.defaultDate} ${currentTimeValue}`, this.dateTimeFormat));
                            }
                            break;
                        default:
                            throw new Error("The provided 'componentState' is not a valid option");
                    }
                } else {
                    this.value(null);
                }
            }
        });

        this.dateValue = ko.computed({
            owner: this,
            read: (): Moment => {
                return DateTimePickerServices.FormatDateTimeComputedRead(this.value, this.hasSetDateProperty, this.configuration.ComponentState);
            },
            write: (value: Moment) => {
                this.hasSetDateProperty(value != null);

                let currentTimeValue = this.defaultTime,
                    currentDateValue = value ? value.format(DateTimeFormatterService.InternalMomentDateFormat) : this.defaultDate;

                if (this.value()) {
                    currentTimeValue = this.value().format(DateTimeFormatterService.InternalMomentTimeFormat);
                }

                if (value) {
                    switch (this.configuration.ComponentState) {
                        case DateTimeWorkflowStates.DateMode:
                            // Important    :   Don't need to set the time, just autofill it as the start of current day
                            this.value(moment(`${currentDateValue} ${this.defaultTime}`, this.dateTimeFormat));
                            break;
                        case DateTimeWorkflowStates.TimeMode:
                            // Important    :   Don't need to set the date, just autofill it as today's date.
                            this.value(moment(`${this.defaultDate} ${currentTimeValue}`, this.dateTimeFormat));
                            break;
                        case DateTimeWorkflowStates.DateTimeMode:
                            // Important    :   If the time has been set then ensure the value is set to the assigned
                            //                  object. In the event it's not been set use the start time of today in
                            //                  order to ensure the moment object is valid.
                            if (this.hasSetTimeProperty()) {
                                this.value(moment(`${currentDateValue} ${currentTimeValue}`, this.dateTimeFormat));
                            } else {
                                this.value(moment(`${currentDateValue} ${this.defaultTime}`, this.dateTimeFormat));
                            }
                            break;
                        default:
                            throw new Error("The provided 'componentState' is not a valid option");
                    }
                } else {
                    this.value(null);
                }
            }
        });

        this.formattedDatePlaceholderLabel = ko.computed(() => {
            return ` (${this.DateFormatPlaceholder})`;
        });

        this.formattedTimePlaceholderLabel = ko.computed(() => {
            return ` (${this.TimeFormatPlaceholder})`;
        });
    };

    private SubscribeMethods(): void {
        this.subs.push(this.dateValue.subscribe((value: Moment) => {
            if (this.configuration.DateConfiguration.IsReadonly) {
                this.ReadonlyDateValue(this.ParseDateToReadOnlyValue(value));
            }
            this.ConfigureDateTimeProperties();
        }));

        this.subs.push(this.timeValue.subscribe((value: Moment) => {
            if (this.configuration.TimeConfiguration.IsReadonly) {
                this.ReadonlyTimeValue(this.ParseTimeToReadOnlyValue(value));
            }
            this.ConfigureDateTimeProperties();
        }));
    };

    private RetrieveCustomMessages(): void {
        this.CustomMessages = {
            ClearDateTime: Deps.LocalisationHelper.getString("GenericResources", "ButtonClearDateTime")
        };
    };

    private SetupValidation(): void {
        const validationConfiguration = <DateTimeValidationConfiguration>{
            HasSetDateProperty: this.hasSetDateProperty,
            HasSetTimeProperty: this.hasSetTimeProperty,
            ComponentState: this.configuration.ComponentState,
            ValidationDateTime: this.configuration.ValidationDateTime,
            IsDateRequired: this.configuration.DateConfiguration.IsRequired,
            IsTimeRequired: this.configuration.TimeConfiguration.IsRequired,
            AllowedMinutes: Deps.TimePickerServices.ConfigureEnabledMinutes(this.configuration.TimeConfiguration),
            AllowedMinutesValidationMessage: this.configuration.TimeConfiguration.MinutesStepValidationMessage
        };

        ValidationService.AddValidationRule(this.value, this.validationFields, {
            validation: DateTimePickerValidationServices.ConfigureValidationRules(validationConfiguration)
        });
    };

    private removeValidationFields() {
        ValidationService.RemoveValidationRule(this.value, this.validationFields);
    };

    public dispose() {
        this.removeValidationFields();
        _.each(this.subs, s => s.dispose());
    };
}

const standardComponent = "datetime-picker";
ko.components.unregister(standardComponent);
ko.components.register(standardComponent, {
    viewModel: DateTimePicker,
    template: { require: `text!Content/ts/Shared/Controls/DateTime/${standardComponent}.html` }
});

const datePickerNoLabelsComponent = "date-picker-nolabels";
ko.components.unregister(datePickerNoLabelsComponent);
ko.components.register(datePickerNoLabelsComponent, {
    viewModel: DateTimePicker,
    template: { require: `text!Content/ts/Shared/Controls/DateTime/${datePickerNoLabelsComponent}.html` }
});

const noinput= "date-picker-noinput";
ko.components.unregister(noinput);
ko.components.register(noinput, {
    viewModel: DateTimePicker,
    template: { require: `text!Content/ts/Shared/Controls/DateTime/${noinput}.html` }
});